/**
 * CFG-02 (owner decision 2026-10-12): ONE Duplicate action.
 *
 * The admin event page used to carry two buttons, "Duplicate" (one copy, with a CTA picker) and
 * "To dates…" (scheduled copies on several dates). They are merged here: the dialog makes one copy
 * by default, or — when the operator adds dates — one scheduled copy per date. Each mode calls
 * exactly what its old button called, so the server behaviour is unchanged:
 *   - one copy      → onDuplicateOne(selected CTA ids)      → duplicate_event(p_dates NULL, p_cta_ids …)
 *   - several dates → onDuplicateToDates(dates, autoQR)     → duplicate_event(p_dates …, p_cta_ids NULL)
 * Who may duplicate is still decided by the server (duplicate_event refuses an MSR, mig 402; a Regional / Territory
 * Manager duplicates only an event they can see, mig 1560).
 * `canPublishQr` false (RM / TM): their browser publishes no QR, so the auto-QR choice is not offered; the copies wait
 * for the events team's QR (a copy that gets a generic CTA is saved Incomplete by the server).
 */
import React, { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";

export const MAX_DUPLICATE_DATES = 24;

export interface DuplicateDialogCta {
  id: string;
  name: string;
  cta_type?: string | null;
}

/** Add one YYYY-MM-DD date to a list: rejects a malformed value, ignores a repeat, keeps the list sorted. */
export function addDuplicateDate(
  dates: string[],
  value: string,
): { dates: string[]; error: string | null } {
  const d = value.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  // Round-trip through a local date so an impossible day (2026-02-30) is refused, not rolled over.
  const real =
    m !== null &&
    (() => {
      const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
      const dt = new Date(y, mo - 1, day);
      return (
        dt.getFullYear() === y &&
        dt.getMonth() === mo - 1 &&
        dt.getDate() === day
      );
    })();
  if (!real) {
    return { dates, error: "Pick a date first." };
  }
  if (dates.includes(d)) {
    return { dates, error: null };
  }
  if (dates.length >= MAX_DUPLICATE_DATES) {
    return {
      dates,
      error: `At most ${MAX_DUPLICATE_DATES} dates per duplicate.`,
    };
  }
  return { dates: [...dates, d].sort(), error: null };
}

function formatDate(d: string): string {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface Props {
  isOpen: boolean;
  eventTitle?: string;
  ctas: DuplicateDialogCta[];
  isWorking: boolean;
  /** Progress of the auto-QR pass after a copy-to-dates (null when not running). */
  qrProgress: { current: number; total: number } | null;
  /** false for a Regional / Territory Manager: no auto-QR (the events team publishes the QR codes). Default true. */
  canPublishQr?: boolean;
  onClose: () => void;
  onDuplicateOne: (ctaIds: string[]) => void;
  onDuplicateToDates: (dates: string[], autoQR: boolean) => void;
}

export const DuplicateEventDialog: React.FC<Props> = ({
  isOpen,
  eventTitle,
  ctas,
  isWorking,
  qrProgress,
  canPublishQr = true,
  onClose,
  onDuplicateOne,
  onDuplicateToDates,
}) => {
  const [selectedCtaIds, setSelectedCtaIds] = useState<Set<string>>(
    () => new Set(ctas.map((c) => c.id)),
  );
  const [dates, setDates] = useState<string[]>([]);
  const [pending, setPending] = useState("");
  const [dateError, setDateError] = useState<string | null>(null);
  const [autoQR, setAutoQR] = useState(true);

  if (!isOpen) {
    return null;
  }

  // A date still in the picker counts too (it is added on submit).
  const shownCount =
    dates.length + (pending !== "" && !dates.includes(pending) ? 1 : 0);
  const toDates = shownCount > 0;
  const allSelected = selectedCtaIds.size === ctas.length;

  const addPending = () => {
    const res = addDuplicateDate(dates, pending);
    setDates(res.dates);
    setDateError(res.error);
    if (!res.error) {
      setPending("");
    }
  };

  const submit = () => {
    // Internal review P2-1: a date picked but not yet added counts -- it is added first, so clicking Duplicate
    // with a date in the picker never silently makes an ordinary one-off copy (or drops that date).
    let list = dates;
    if (pending !== "") {
      const res = addDuplicateDate(dates, pending);
      if (res.error) {
        setDateError(res.error);
        return;
      }
      list = res.dates;
      setDates(list);
      setPending("");
    }
    if (list.length > 0) {
      onDuplicateToDates(list, canPublishQr && autoQR);
    } else {
      onDuplicateOne(Array.from(selectedCtaIds));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-event-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3
            id="duplicate-event-title"
            className="text-lg font-semibold text-gray-900"
          >
            Duplicate Event
          </h3>
          <p className="text-sm text-gray-500">
            Creating {toDates ? "scheduled copies" : "a copy"} of{" "}
            <span className="font-medium">{eventTitle}</span>
          </p>
        </div>

        {/* Optional: copy to specific dates. Empty = one copy. */}
        <div className="space-y-2">
          <label
            htmlFor="duplicate-date-input"
            className="block text-sm font-medium text-gray-700"
          >
            Copy to specific dates{" "}
            <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <p className="text-xs text-gray-500">
            Leave empty for one copy. Each date you add creates a scheduled copy
            at the same time of day (up to {MAX_DUPLICATE_DATES}). Send history,
            attendees, vouchers and email tracking are not copied.
          </p>
          <div className="flex gap-2">
            <input
              id="duplicate-date-input"
              type="date"
              value={pending}
              onChange={(e) => {
                setPending(e.target.value);
                setDateError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addPending();
                }
              }}
              disabled={isWorking}
              className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={addPending}
              disabled={isWorking || pending === ""}
            >
              Add date
            </Button>
          </div>
          {dateError && (
            <p className="text-xs text-red-600" role="alert">
              {dateError}
            </p>
          )}
          {dates.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label="Dates to copy to">
              {dates.map((d) => (
                <li
                  key={d}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-50 border border-primary-200 px-2 py-0.5 text-xs text-primary-900"
                >
                  {formatDate(d)}
                  <button
                    type="button"
                    onClick={() => setDates(dates.filter((x) => x !== d))}
                    disabled={isWorking}
                    aria-label={`Remove ${d}`}
                    className="text-primary-700 hover:text-primary-900"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {toDates ? (
          <>
            {ctas.length > 0 && (
              <p className="text-xs text-gray-600">
                Every Call to Action on this event is copied to each date.
              </p>
            )}
            {!canPublishQr ? (
              <p
                className="text-xs text-gray-600"
                data-testid="duplicate-qr-by-events-team"
              >
                Any QR codes the copies need are published by the events team.
              </p>
            ) : (
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoQR}
                  onChange={(e) => setAutoQR(e.target.checked)}
                  disabled={isWorking}
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium">
                    Auto-generate QR codes for cloned CTAs
                  </span>
                  <span className="block text-xs text-gray-500">
                    Recommended. Generates composite QRs for each cloned CTA on
                    the new scheduled events.
                  </span>
                </span>
              </label>
            )}
            {qrProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
                Generating QRs… {qrProgress.current} / {qrProgress.total}{" "}
                event(s) done
              </div>
            )}
          </>
        ) : (
          ctas.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Include CTAs:
                </span>
                <button
                  type="button"
                  disabled={isWorking}
                  onClick={() =>
                    setSelectedCtaIds(
                      allSelected ? new Set() : new Set(ctas.map((c) => c.id)),
                    )
                  }
                  className="text-xs text-primary-600 hover:text-primary-800"
                >
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {ctas.map((cta) => (
                  <label
                    key={cta.id}
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCtaIds.has(cta.id)}
                      disabled={isWorking}
                      onChange={() => {
                        const next = new Set(selectedCtaIds);
                        if (next.has(cta.id)) {
                          next.delete(cta.id);
                        } else {
                          next.add(cta.id);
                        }
                        setSelectedCtaIds(next);
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">
                        {cta.name}
                      </span>
                      {cta.cta_type && (
                        <span className="ml-2 text-gray-500 capitalize">
                          ({cta.cta_type})
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {canPublishQr
                  ? "QR codes will be regenerated for copied CTAs."
                  : "QR codes for the copy are published by the events team."}
              </p>
            </div>
          )
        )}
        {/* external audit 08e03a1c3: an RM / TM is told who publishes QR codes even with no CTA to pick */}
        {!toDates && !canPublishQr && ctas.length === 0 && (
          <p
            className="text-xs text-gray-600"
            data-testid="duplicate-qr-by-events-team-one"
          >
            Any QR codes the copy needs are published by the events team.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isWorking}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={isWorking}
          >
            {isWorking
              ? "Working…"
              : toDates
                ? `Duplicate to ${shownCount} date${shownCount === 1 ? "" : "s"}`
                : "Duplicate"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateEventDialog;
